import {
  BRIDGE_VERSION,
  INBOUND_TYPES,
  BridgeMessage,
  InitMessage,
  MenuAppLike,
  UrlParams,
} from './bridgeMessages';

/**
 * Flatten `window.location.search` + `window.location.hash` into a flat map.
 * Search wins over hash on duplicate keys. A leading `#`, `/`, or `?` on the
 * hash (legacy PISP deep-link `#/?jwt=…`) is stripped before parsing.
 */
export const parseUrlParams = (search: string, hash: string): UrlParams => {
  const out: UrlParams = {};
  new URLSearchParams(search).forEach((v, k) => {
    out[k] = v;
  });
  const cleanedHash = hash.replace(/^#\/?\??/, '');
  if (cleanedHash) {
    new URLSearchParams(cleanedHash).forEach((v, k) => {
      if (!(k in out)) out[k] = v;
    });
  }
  return out;
};

/** Build the iframe URL for a flow, optionally deep-linked to a subPath. */
export const computeIframeSrc = (formsHostUrl: string, flowId: string, subPath: string): string => {
  const base = `${formsHostUrl}/flows/${flowId}`;
  return subPath ? `${base}/${subPath}` : base;
};

/** Post-submit destination for a flow, falling back when unmapped. */
export const resolveSubmitSuccessRoute = (
  flowId: string | undefined,
  routes: Record<string, string>,
  fallback: string,
): string => (flowId && routes[flowId]) || fallback;

/** Safely parse an inbound message; returns null unless it has a string `type`. */
export const parseBridgeMessage = (data: unknown): BridgeMessage | null => {
  let msg: unknown;
  if (typeof data === 'string') {
    try {
      msg = JSON.parse(data);
    } catch {
      return null;
    }
  } else {
    msg = data;
  }
  if (!msg || typeof msg !== 'object') return null;
  const candidate = msg as Record<string, unknown>;
  if (typeof candidate.type !== 'string') return null;
  return candidate as BridgeMessage;
};

/** Find a CUSTOM_FLOW app matching the flowId across the supplied app lists. */
export const findCustomFlowApp = <T extends MenuAppLike>(
  apps: T[],
  flowId: string | undefined,
): T | undefined =>
  apps.find((app) => app?.appSource === 'CUSTOM_FLOW' && app?.marketplaceKey === flowId);

/** Build the `init` payload sent to the iframe. */
export const buildInitMessage = (args: {
  token: string | null;
  tenant?: string;
  locale: string;
  theme: Record<string, unknown>;
  flowId?: string;
  subPath: string;
  urlParams: UrlParams;
}): InitMessage => ({
  type: INBOUND_TYPES.INIT,
  v: BRIDGE_VERSION,
  token: args.token,
  tenant: args.tenant,
  locale: args.locale || 'en',
  theme: args.theme || {},
  flowId: args.flowId,
  // A single-segment subPath is a step id to land on. Multi-segment
  // master->detail deep links route from the iframe src instead.
  entryStep: args.subPath && !args.subPath.includes('/') ? args.subPath : undefined,
  input: { ...args.urlParams, searchParams: args.urlParams },
});
